import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { BookOpen, Check, Globe, Shield, Users, Zap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const registerSchema = z
  .object({
    firstName: z.string().min(2, "First name must be at least 2 characters"),
    lastName: z.string().min(2, "Last name must be at least 2 characters"),
    email: z.string().email("Please enter a valid email address"),
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().min(6, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type LoginForm = z.infer<typeof loginSchema>;
type RegisterForm = z.infer<typeof registerSchema>;

export default function AuthPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState<"login" | "register">("login");
  const { toast } = useToast();

  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const registerForm = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  // login is handled via useAuth.login in handleLogin

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterForm) => {
      const { firstName, lastName, email, password } = data;
      const response = await apiRequest("/api/auth/register", "POST", {
        firstName,
        lastName,
        email,
        password,
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Account Created Successfully",
        description: "You can now sign in with your credentials.",
        variant: "default",
      });
      setMode("login");
      loginForm.setValue("email", registerForm.getValues("email"));
    },
    onError: (error: any) => {
      toast({
        title: "Registration Failed",
        description: error.message || "An error occurred during registration. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleLogin = loginForm.handleSubmit(async (data: LoginForm) => {
    try {
      await login(data.email, data.password);
      // redirect to dashboard on successful login
      navigate("/dashboard");
    } catch (error: any) {
      toast({
        title: "Sign In Failed",
        description: error.message || "Invalid email or password. Please check your credentials and try again.",
        variant: "destructive",
      });
    }
  });

  const handleRegister = (data: RegisterForm) => {
    registerMutation.mutate(data);
  };

  const handleGoogleAuth = () => {
    window.location.href = "/auth/google";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        <div className="flex lg:flex-row flex-col bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden min-h-[600px]">
          {/* Left Side - Brand Info */}
          <div className="lg:flex-1 bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 lg:p-12 p-6 flex flex-col justify-center relative overflow-hidden lg:min-h-[600px] min-h-[200px]">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 animate-pulse"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4 lg:mb-6">
                <div className="p-2 lg:p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <BookOpen className="h-6 w-6 lg:h-8 lg:w-8 text-white" />
                </div>
                <h1 className="text-2xl lg:text-4xl font-bold text-white">StoryLikho</h1>
              </div>

              <p className="text-lg lg:text-xl text-white/90 mb-6 lg:mb-8 leading-relaxed">
                Transform your ideas into compelling stories with AI-powered
                writing assistance
              </p>

              <div className="space-y-3 lg:space-y-4 hidden lg:block">
                <div className="flex items-center gap-3 text-white/80 opacity-0 animate-[slideIn_0.6s_ease-out_0.1s_forwards]">
                  <div className="w-6 h-6 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                  <span>AI-powered story generation</span>
                </div>

                <div className="flex items-center gap-3 text-white/80 opacity-0 animate-[slideIn_0.6s_ease-out_0.2s_forwards]">
                  <div className="w-6 h-6 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                  <span>Character and world building tools</span>
                </div>

                <div className="flex items-center gap-3 text-white/80 opacity-0 animate-[slideIn_0.6s_ease-out_0.3s_forwards]">
                  <div className="w-6 h-6 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                  <span>Multiple story formats support</span>
                </div>

                <div className="flex items-center gap-3 text-white/80 opacity-0 animate-[slideIn_0.6s_ease-out_0.4s_forwards]">
                  <div className="w-6 h-6 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                  <span>Professional publishing tools</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Auth Form */}
          <div className="lg:flex-1 bg-white/95 backdrop-blur-sm lg:p-12 p-6 flex flex-col justify-center">
            {/* Toggle Buttons */}
            <div className="flex bg-purple-100 rounded-2xl p-1 mb-6 lg:mb-8">
              <button
                className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${
                  mode === "login"
                    ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg"
                    : "text-gray-600 hover:text-purple-600"
                }`}
                onClick={() => setMode("login")}
              >
                Sign In
              </button>
              <button
                className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${
                  mode === "register"
                    ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg"
                    : "text-gray-600 hover:text-purple-600"
                }`}
                onClick={() => setMode("register")}
              >
                Sign Up
              </button>
            </div>

            {/* Google Auth */}
            <div className="mb-4 lg:mb-6">
              <p className="text-center text-gray-600 mb-3 lg:mb-4 font-medium">
                Continue with
              </p>
              <Button
                onClick={handleGoogleAuth}
                variant="outline"
                className="w-full h-12 lg:h-12 border-2 border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 text-sm lg:text-base"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    fill="#4285f4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34a853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#fbbc05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.2 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#ea4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>
            </div>

            {/* Divider */}
            <div className="relative my-4 lg:my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-purple-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">or</span>
              </div>
            </div>

            {/* Login Form */}
            {mode === "login" && (
              <form
                onSubmit={handleLogin}
                className="space-y-3 lg:space-y-4 animate-[fadeIn_0.3s_ease-in-out]"
              >
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    {...loginForm.register("email")}
                    className="h-12 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 transition-all duration-300 text-base"
                    placeholder="Enter your email"
                  />
                  {loginForm.formState.errors.email && (
                    <p className="text-red-500 text-sm">
                      {loginForm.formState.errors.email.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="password"
                    className="text-gray-700 font-medium"
                  >
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    {...loginForm.register("password")}
                    className="h-12 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 transition-all duration-300 text-base"
                    placeholder="Enter your password"
                  />
                  {loginForm.formState.errors.password && (
                    <p className="text-red-500 text-sm">
                      {loginForm.formState.errors.password.message}
                    </p>
                  )}
                </div>


                <Button
                  type="submit"
                  disabled={loginForm.formState.isSubmitting}
                  className="w-full h-12 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 text-white font-semibold text-base"
                >
                  {loginForm.formState.isSubmitting
                    ? "Signing in..."
                    : "Sign In"}
                </Button>

                <div className="text-center mt-4">
                  <a
                    href="#"
                    className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                  >
                    Forgot your password?
                  </a>
                </div>
              </form>
            )}

            {/* Register Form */}
            {mode === "register" && (
              <form
                onSubmit={registerForm.handleSubmit(handleRegister)}
                className="space-y-3 lg:space-y-4 animate-[fadeIn_0.3s_ease-in-out]"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="firstName"
                      className="text-gray-700 font-medium"
                    >
                      First Name
                    </Label>
                    <Input
                      id="firstName"
                      {...registerForm.register("firstName")}
                      className="h-12 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 transition-all duration-300 text-base"
                      placeholder="First name"
                    />
                    {registerForm.formState.errors.firstName && (
                      <p className="text-red-500 text-sm">
                        {registerForm.formState.errors.firstName.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="lastName"
                      className="text-gray-700 font-medium"
                    >
                      Last Name
                    </Label>
                    <Input
                      id="lastName"
                      {...registerForm.register("lastName")}
                      className="h-12 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 transition-all duration-300 text-base"
                      placeholder="Last name"
                    />
                    {registerForm.formState.errors.lastName && (
                      <p className="text-red-500 text-sm">
                        {registerForm.formState.errors.lastName.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    {...registerForm.register("email")}
                    className="h-12 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 transition-all duration-300 text-base"
                    placeholder="Enter your email"
                  />
                  {registerForm.formState.errors.email && (
                    <p className="text-red-500 text-sm">
                      {registerForm.formState.errors.email.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="password"
                    className="text-gray-700 font-medium"
                  >
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    {...registerForm.register("password")}
                    className="h-12 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 transition-all duration-300 text-base"
                    placeholder="Create a password"
                  />
                  {registerForm.formState.errors.password && (
                    <p className="text-red-500 text-sm">
                      {registerForm.formState.errors.password.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="confirmPassword"
                    className="text-gray-700 font-medium"
                  >
                    Confirm Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    {...registerForm.register("confirmPassword")}
                    className="h-12 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 transition-all duration-300 text-base"
                    placeholder="Confirm your password"
                  />
                  {registerForm.formState.errors.confirmPassword && (
                    <p className="text-red-500 text-sm">
                      {registerForm.formState.errors.confirmPassword.message}
                    </p>
                  )}
                </div>


                <Button
                  type="submit"
                  disabled={registerMutation.isPending}
                  className="w-full h-12 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 text-white font-semibold text-base"
                >
                  {registerMutation.isPending
                    ? "Creating Account..."
                    : "Create Account"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
